import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { fetchSubTaskById } from "./subTaskService";
import SubTaskFileAttachments from "./SubTaskFileAttachments";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import "../assets/css/ViewCustomer.css";
import "../assets/css/EditCustomer.css";
import "../assets/css/Projects.css";
import "../assets/css/Tasks.css";

function ViewSubTask({ onBack }) {
  const navigate = useNavigate();
  const { id: projectId, subprojectId, userStoryId, taskId, subTaskId } = useParams();
  const hasSubproject = Boolean(subprojectId);
  const role = useMemo(() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canEditSubTask = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const [subTask, setSubTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      if (!subTaskId) return;
      setLoading(true);
      setError("");
      try {
        const data = await fetchSubTaskById(subTaskId);
        setSubTask(data || null);
        if (!data) setError("Subtask not found.");
      } catch (err) {
        console.error("Error fetching subtask:", err);
        setError(err.response?.data?.message || "Failed to load subtask.");
        setSubTask(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [subTaskId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const goBack = () => {
    if (onBack) onBack();
    else if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/user-stories/${userStoryId}/tasks/${taskId}/view`);
    } else {
      navigate(`/projects/${projectId}/user-stories/${userStoryId}/tasks/${taskId}/view`);
    }
  };

  const handleEdit = () => {
    if (hasSubproject) {
      navigate(`/projects/${projectId}/subprojects/${subprojectId}/user-stories/${userStoryId}/tasks/${taskId}/subtasks/${subTaskId}/edit`);
    } else {
      navigate(`/projects/${projectId}/user-stories/${userStoryId}/tasks/${taskId}/subtasks/${subTaskId}/edit`);
    }
  };

  const refreshSubTask = useCallback(async () => {
    if (!subTaskId) return;
    try {
      const data = await fetchSubTaskById(subTaskId);
      if (data) setSubTask(data);
    } catch (err) {
      console.error("Error refetching subtask:", err);
    }
  }, [subTaskId]);

  const InfoItem = ({ label, value }) => {
    const hasValue = value != null && value !== "" && value !== "—";
    return (
      <div className="viewlead-info-item">
        <span className={`viewlead-info-label ${hasValue ? "has-value" : ""}`}>{label}: </span>
        <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>{hasValue ? value : "—"}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading subtask...</div>
      </div>
    );
  }

  if (error || !subTask) {
    return (
      <div className="viewlead-container">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <div
          className="error-message"
          style={{ backgroundColor: "#fee", color: "#c33", padding: "12px", borderRadius: "4px", marginTop: "16px" }}
        >
          {error || "Subtask not found."}
        </div>
      </div>
    );
  }

  const t = subTask;
  const priority = t.Priority ?? t.priority ?? "";
  const status = t.Status ?? t.status ?? "";

  return (
    <div className="viewlead-container">
      <div className="viewlead-header">
        <button type="button" className="viewlead-back-link" onClick={goBack}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" className="btn-subproject-primary" onClick={handleEdit}>
          Edit Subtask
        </button>
      </div>

      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "stretch",
          flexWrap: "nowrap",
          marginBottom: 20
        }}
      >
        <div className="viewlead-card" style={{ flex: "2 1 0", minWidth: 260 }}>
          <h3 className="viewlead-section-title">Subtask Information</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Subtask ID" value={String(t.Sub_Task_Id ?? t.sub_task_id ?? "")} />
            <InfoItem label="Title" value={t.Title || t.title} />
            <InfoItem label="Status" value={status} />
            <InfoItem label="Priority" value={priority || "Not Set"} />
            <InfoItem label="Start Date" value={formatDate(t.Start_Date || t.start_date)} />
            <InfoItem label="End Date" value={formatDate(t.End_Date || t.end_date)} />
            <InfoItem label="Task" value={t.Task_Title || t.task_title} />
          </div>

          <h3 className="viewlead-section-title">Description</h3>
          <div className="viewlead-info-grid">
            <InfoItem label="Description" value={t.Description || t.description} />
          </div>
        </div>

        <div className="viewlead-card" style={{ flex: "1 1 0", minWidth: 260, maxWidth: 480 }}>
          <h3 className="viewlead-section-title">Assigned Users</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {(t.Assigned_Users || t.assigned_users || []).length > 0 ? (
              (t.Assigned_Users || t.assigned_users || []).map((u, idx) => {
                const name = u.User_Name ?? u.user_name ?? u.name ?? "—";
                return (
                  <div key={u.User_Id ?? u.user_id ?? idx} className="viewlead-info-item">
                    <span className="viewlead-info-value">{name}</span>
                  </div>
                );
              })
            ) : (
              <div className="viewlead-info-item">
                <span className="viewlead-info-value muted">—</span>
              </div>
            )}
          </div>
          <h3 className="viewlead-section-title">Audit</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <InfoItem label="Created By" value={t.Created_By_Name || t.created_by_name} />
            <InfoItem label="Created On" value={formatDate(t.Created_On || t.created_on)} />
            <InfoItem label="Updated By" value={t.Updated_By_Name || t.updated_by_name} />
            <InfoItem label="Updated On" value={formatDate(t.Updated_On || t.updated_on)} />
          </div>
        </div>
      </div>

      <div className="viewlead-card issue-attachments-card project-attachments-card">
        <SubTaskFileAttachments
          subTask={t}
          subTaskId={subTaskId}
          canUpload={canEditSubTask}
          title="Subtask attachments"
          onRefresh={refreshSubTask}
        />
      </div>
    </div>
  );
}

export default ViewSubTask;

